import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { Input } from "@/modern/components/ui/input";
import { BryntumWidget } from "@bryntum/schedulerpro-react";
import { ObjectHelper, Store } from "@bryntum/schedulerpro";
import { classNameHelper, lineThroughString } from "@/other/utils/utils";
import { cn } from "@/modern/utils";

/**
 * Column configuration interface for EntityTagEditor
 * These properties are provided by columns that use this editor
 */
export interface EntityTagColumn {
  swivuListTitle: string;
  swivuListItems: any[];
  canMultiSelectSwivuList?: boolean;
  getSelectedSwivuListItem: (record: any) => any;
  onSwivuListSelectionChange: (
    record: any,
    selectedItems: any[],
    editor: any,
    clickedItem: any,
    action: "select" | "deselect"
  ) => void;
  onSwivuBeforeListSelectionChange?: (record: any, selectedItems: any[]) => boolean;
  getSwivuListItemDisplayValue: (item: any) => string;
  swivuListSearchKey?: string;
  onSwivuManageList?: (...args: any[]) => void;
  canEmptySwivuList?: boolean;
  popupCls?: string[];
  swivuRecord?: any;
}

interface EntityTagEditorProps {
  record: {
    entityRecord: any;
    column: EntityTagColumn;
    event: MouseEvent;
    [key: string]: any;
  };
  onClose: () => void;
  editorInstance: any;
  context?: any;
}

const EntityTagEditorContent = ({ record, onClose, editorInstance }: EntityTagEditorProps) => {
  const { entityRecord, column, event } = record;
  column.swivuRecord = entityRecord;
  const listRef = useRef<any>(null);
  const [searchValue, setSearchValue] = useState("");
  const [lastSelection, setLastSelection] = useState<any[]>([]);
  const isFirstRender = useRef(true);

  // Set column.swivuRecord for backward compatibility (some columns access this.swivuRecord)
  useEffect(() => {
    column.swivuRecord = entityRecord;
  }, [column, entityRecord]);

  // Extract column configuration
  const {
    canMultiSelectSwivuList = false,
    swivuListTitle,
    getSelectedSwivuListItem,
    // onSwivuManageList,
    swivuListItems,
    getSwivuListItemDisplayValue,
    swivuListSearchKey,
    popupCls = [],
    canEmptySwivuList = true,
    onSwivuBeforeListSelectionChange = () => true,
    onSwivuListSelectionChange
  } = column;

  // Determine if editor should show search or header
  const hasSearch = !!swivuListSearchKey;

  // Bryntum List configuration
  const listConfig = useMemo(() => {
    return {
      type: "list",
      scrollable: false, // Disable Bryntum's own scrolling, use parent container
      multiSelect: canMultiSelectSwivuList,
      cls: "swivu-tag-list swivu-tag-list-enhanced",
      height: "auto", // Let content define height, parent will scroll
      itemTpl: (item: any) =>
        `<span class="swivu-tag-list-item ${classNameHelper(item.disabled && "text-muted-foreground pointer-events-none")}">${lineThroughString(
          getSwivuListItemDisplayValue.call(column, item),
          item.archived
        )}</span>`,
      items: swivuListItems,
      listeners: {
        item: ({ source, record: listItem }: any) => handleItemClick(source, listItem)
      }
    };
  }, [canMultiSelectSwivuList, swivuListItems, getSwivuListItemDisplayValue, column]);

  // Initialize list selection on first render
  useEffect(() => {
    if (listRef.current?.instance && isFirstRender.current) {
      const list = listRef.current.instance;

      // Sort by order field
      if (list.store) {
        list.store.sort({ field: "order" });
      }

      // Select initial items (call with column context)
      const selectedItem = getSelectedSwivuListItem.call(column, entityRecord);
      if (selectedItem) {
        list.select(selectedItem);
      }

      setLastSelection(ObjectHelper.clone(list.selected?.values || []));
      isFirstRender.current = false;
    }
  }, [entityRecord, getSelectedSwivuListItem, column]);

  // Handle search input
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);

      if (listRef.current?.instance) {
        const list = listRef.current.instance;
        const store = list.store;

        // Detaching data store from list to prevent UI lag
        list.store = new Store();
        store.clearFilters();

        if (value.trim() && swivuListSearchKey) {
          store.filterBy((record: any) =>
            (record[swivuListSearchKey] || "")
              .toLowerCase()
              .includes(value.trim().toLowerCase())
          );
        }

        list.store = store;
      }
    },
    [swivuListSearchKey]
  );

  // Handle clear search
  const handleSearchClear = useCallback(() => {
    setSearchValue("");
    handleSearchChange({ target: { value: "" } } as any);
  }, [handleSearchChange]);

  // Determine user action (select/deselect)
  const getSwivuUserAction = useCallback((previousSelection: any, clickedItem: any): "select" | "deselect" => {
    const formatValue = (value: any) => {
      if (typeof value === "object") return value.id;
      return value;
    };

    if (!previousSelection) return "select";

    const formattedClickedItem = formatValue(clickedItem);
    const isAlreadySelected = Array.isArray(previousSelection)
      ? previousSelection.map(formatValue).includes(formattedClickedItem)
      : formatValue(previousSelection) === formattedClickedItem;

    return isAlreadySelected ? "deselect" : "select";
  }, []);

  // Handle item click
  const handleItemClick = useCallback(
    (source: any, listItem: any) => {
      // Prevent selection of disabled items
      if (listItem.disabled) {
        source.deselectAll();
        const currentSelection = getSelectedSwivuListItem.call(column, entityRecord);
        if (currentSelection) {
          source.select(currentSelection);
        }
        return;
      }

      const selected = source.selected.values;
      const previousSelection = getSelectedSwivuListItem.call(column, entityRecord);

      // Call validation callback (call with column context)
      if (onSwivuBeforeListSelectionChange.call(column, entityRecord, selected)) {
        const action = getSwivuUserAction(previousSelection, listItem);

        // Handle toggle for single-select
        if (!source.multiSelect && lastSelection.length) {
          const isClickedAgain = lastSelection.find((item: any) => item.id === listItem.id);
          if (isClickedAgain && canEmptySwivuList) {
            source.deselect(listItem);
          } else {
            source.select(listItem);
          }
        }

        // Call column's selection change callback (call with column context)
        onSwivuListSelectionChange.call(
          column,
          entityRecord,
          selected,
          { swivuRefreshListItems: (items?: any[]) => refreshListItems(items) },
          listItem,
          action
        );

        // Auto-close if single-select
        if (!canMultiSelectSwivuList) {
          onClose();
        }
      } else {
        // Validation failed, restore previous selection
        source.deselectAll();
        if (previousSelection) {
          source.select(previousSelection);
        }
      }

      setLastSelection(ObjectHelper.clone(selected));
    },
    [
      column,
      entityRecord,
      getSelectedSwivuListItem,
      onSwivuBeforeListSelectionChange,
      getSwivuUserAction,
      lastSelection,
      canEmptySwivuList,
      onSwivuListSelectionChange,
      canMultiSelectSwivuList,
      onClose
    ]
  );

  // Refresh list items (exposed to column callbacks)
  const refreshListItems = useCallback((items?: any[]) => {
    if (listRef.current?.instance) {
      listRef.current.instance.items = items || swivuListItems;
    }
  }, [swivuListItems]);

  // Handle manage button click (disabled for now)
  // const handleManageClick = useCallback(() => {
  //   if (onSwivuManageList) {
  //     onSwivuManageList();
  //     setTimeout(() => {
  //       onClose();
  //     }, 100);
  //   }
  // }, [onSwivuManageList, onClose]);

  // Custom header renderer
  const headerRenderer = () => {
    if (hasSearch) {
      return (
        <div className="flex flex-col border-b border-border bg-card">
          <div className="relative p-4">
            <i className="fa fa-search absolute left-7 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none text-sm" />
            <Input
              type="text"
              placeholder={swivuListTitle}
              value={searchValue}
              onChange={handleSearchChange}
              className="pl-9 pr-20 h-swivu-input text-sm"
              autoFocus
            />
            <button
              onClick={onClose}
              className="absolute right-7 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded bg-muted text-muted-foreground hover:bg-muted-foreground hover:text-background transition-colors"
            >
              ESC
            </button>
            {searchValue && (
              <button
                onClick={handleSearchClear}
                className="absolute right-24 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <i className="fa fa-times text-sm" />
              </button>
            )}
          </div>
        </div>
      );
    }

    // Simple header with title for non-search mode
    return (
      <div className="drag-handle flex items-center justify-between p-4 border-b border-border bg-card">
        <h2 className="text-sm font-semibold text-foreground m-0">{swivuListTitle}</h2>
        <button
          onClick={onClose}
          className="size-8 hover:bg-secondary rounded-swivu-base transition-colors bg-transparent border-0 cursor-pointer flex items-center justify-center"
        >
          <i className="text-muted-foreground fas fa-times text-sm"></i>
        </button>
      </div>
    );
  };

  // Handle click outside to close (shadcn/Radix UI way)
  const handleInteractOutside = useCallback((_e: Event) => {
    // Close modal when clicking outside
    onClose();
  }, [onClose]);

  return (
    <BaseModal
      record={{ isPhantom: false, archived: false }}
      onClose={onClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "26rem",
        height: "24rem",
        minWidth: "24rem",
        minHeight: "14rem",
        maxWidth: "28rem",
        maxHeight: "min(90vh, 28rem)"
      }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
      headerRenderer={headerRenderer}
      className={cn(popupCls.join(" "), "!resize-none entity-tag-editor-modal")}
      defaultPosition={{ x: event.x, y: event.y }}
      onInteractOutside={handleInteractOutside}
      zIndex={"z-[--swivu-bryntum-z-index]"}
    >
      <BaseModalBody className="!p-0 h-full" contentElementClassName="!p-0 h-full">
        <div className="h-full overflow-y-auto overflow-x-hidden">
          {swivuListItems?.length > 0 ? (
            <>
              {/* @ts-expect-error - Bryntum Widget types */}
              <BryntumWidget ref={listRef} type="list" config={listConfig} />
            </>
          ) : (
            <div className="flex items-center justify-center p-8 text-sm text-muted-foreground min-h-[200px]">
              No data yet.
            </div>
          )}
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};

export const EntityTagEditor = (props: EntityTagEditorProps) => {
  return <EntityTagEditorContent {...props} />;
};
